import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AddStore from "../store/AddStore";
import axiosGroceryInstance from "../restaurant/AxiosGroceryInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import { GROCERY_API_PATHS } from "../constants/apiPaths";
import EatsAdminLayout from "./EatsAdminLayout";
import EatsAdminCreateStepper from "./EatsAdminCreateStepper";
import EatsAdminPageHeader from "./EatsAdminPageHeader";
import RootAdminUserPicker from "./RootAdminUserPicker";
import { sanitizeAadharInput, validateAadharNumber, formatAadharDisplay, formatAadharDisplayOrFallback, sanitizeMobileInput, validateMobileNumber } from "../restaurant/formatDisplay";
import "./EatsAdminCreatePage.css";

const EatsAdminCreateStorePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.userId || null;

  const [tab, setTab] = useState("store");
  const [pendingStores, setPendingStores] = useState([]);
  const [activePendingStoreId, setActivePendingStoreId] = useState(null);
  const [rootAdminForm, setRootAdminForm] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    aadharNo: "",
    password: "",
  });
  const [creatingRootAdmin, setCreatingRootAdmin] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [rootAdminMode, setRootAdminMode] = useState("new");

  const activePendingStore =
    pendingStores.find((s) => String(s.storeId) === String(activePendingStoreId)) || null;

  useEffect(() => {
    const loadPendingStores = async () => {
      try {
        const res = await axiosGroceryInstance.get(GROCERY_API_PATHS.pendingStores, {
          headers: getAuthHeaders(),
        });
        const sanitized = Array.isArray(res?.data?.stores)
          ? res.data.stores.filter((s) => s && s.storeId)
          : [];
        setPendingStores(sanitized);
        const requestedId = searchParams.get("storeId");
        const activeId =
          requestedId && sanitized.some((s) => String(s.storeId) === String(requestedId))
            ? requestedId
            : sanitized[0]?.storeId || null;
        setActivePendingStoreId(activeId);
        if (activeId) setTab("root-admin");
      } catch (error) {
        console.error("Failed loading pending stores:", error?.response?.data || error?.message);
        setPendingStores([]);
        setActivePendingStoreId(null);
        setTab("store");
      }
    };
    loadPendingStores();
  }, [searchParams]);

  const handleStoreCreated = (result) => {
    const store = result?.store || {};
    if (!store?.storeId) {
      alert("Store created but storeId was not returned. Please retry.");
      return;
    }
    const payload = {
      storeId: store.storeId,
      storeName: store.storeName || "",
      shortCode: store.shortCode || "",
      createdById: currentUserId,
      createdAt: new Date().toISOString(),
    };
    const nextList = [
      payload,
      ...pendingStores.filter((s) => String(s.storeId) !== String(payload.storeId)),
    ];
    setPendingStores(nextList);
    setActivePendingStoreId(payload.storeId);
    setTab("root-admin");
  };

  const selectPendingStore = (storeId) => {
    setActivePendingStoreId(storeId);
    setTab("root-admin");
  };

  const clearPendingStore = (storeId) => {
    const nextList = pendingStores.filter((s) => String(s.storeId) !== String(storeId));
    const nextActiveId =
      String(activePendingStoreId) === String(storeId)
        ? nextList[0]?.storeId || null
        : activePendingStoreId;
    setPendingStores(nextList);
    setActivePendingStoreId(nextActiveId);
    if (!nextActiveId) setTab("store");
  };

  const createRootAdminForStore = async () => {
    if (!activePendingStore?.storeId) {
      alert("Please create/select a store first.");
      return;
    }
    if (!currentUserId) {
      alert("Current user is missing. Please sign in again.");
      return;
    }

    const assigningExisting = rootAdminMode === "existing" && selectedExistingUser?.userId;
    const { firstName, lastName, mobile, email, aadharNo, password } = rootAdminForm;

    if (assigningExisting) {
      setCreatingRootAdmin(true);
      try {
        const payload = {
          userId: selectedExistingUser.userId,
          roleName: "ROOT_ADMIN",
          storeId: activePendingStore.storeId,
          createdById: currentUserId,
        };
        const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.storeUsers, payload, {
          headers: getAuthHeaders(),
        });
        alert(response?.data?.message || "Root admin assigned successfully");
        setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
        setSelectedExistingUser(null);
        setRootAdminMode("new");
        clearPendingStore(activePendingStore.storeId);
        navigate("/eats/admin?tab=stores");
      } catch (error) {
        alert(error?.response?.data?.message || "Failed to assign root admin");
      } finally {
        setCreatingRootAdmin(false);
      }
      return;
    }

    if (!firstName || !lastName || !mobile || !aadharNo?.trim() || !password) {
      alert("Please fill first name, last name, mobile, aadhar number and password.");
      return;
    }
    const mobileError = validateMobileNumber(mobile, { required: true, label: "Mobile" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const aadharError = validateAadharNumber(aadharNo);
    if (aadharError) {
      alert(aadharError);
      return;
    }
    setCreatingRootAdmin(true);
    try {
      const payload = {
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        firstName,
        lastName,
        mobile,
        email,
        aadharNumber: sanitizeAadharInput(aadharNo),
        password,
        roleName: "ROOT_ADMIN",
        storeId: activePendingStore.storeId,
        createdById: currentUserId,
      };
      const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.storeUsers, payload, {
        headers: getAuthHeaders(),
      });
      alert(response?.data?.message || "Root admin user created successfully");
      setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
      setSelectedExistingUser(null);
      setRootAdminMode("new");
      clearPendingStore(activePendingStore.storeId);
      navigate("/eats/admin?tab=stores");
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to create root admin");
    } finally {
      setCreatingRootAdmin(false);
    }
  };

  const handleSelectExistingRootAdmin = (user) => {
    setSelectedExistingUser(user);
    setRootAdminMode("existing");
    const nameParts = String(user?.name || "").trim().split(/\s+/);
    const firstName = user?.firstName || nameParts[0] || "";
    const lastName = user?.lastName || nameParts.slice(1).join(" ") || "";
    setRootAdminForm({
      firstName,
      lastName,
      mobile: user?.mobile || user?.contactNumber || "",
      email: user?.email || "",
      aadharNo: user?.aadharNo || "",
      password: "",
    });
  };

  const clearExistingRootAdminSelection = () => {
    setSelectedExistingUser(null);
    setRootAdminMode("new");
    setRootAdminForm({ firstName: "", lastName: "", mobile: "", email: "", aadharNo: "", password: "" });
  };

  return (
    <EatsAdminLayout activeTab="stores">
      <div className="eats-admin-create-page">
        <EatsAdminPageHeader
          title="Create Store"
          subtitle="Create store first, then assign root admin user."
          backTo="/eats/admin?tab=stores"
        />

        <EatsAdminCreateStepper
          activeStepId={tab}
          onStepChange={(next) => {
            if (next === "root-admin" && !activePendingStoreId) return;
            setTab(next);
          }}
          steps={[
            { id: "store", label: "Create Store" },
            {
              id: "root-admin",
              label: "Create Root Admin",
              disabled: !activePendingStoreId,
            },
          ]}
        />

        {pendingStores.length > 0 ? (
          <div className="eats-admin-pending-panel">
            <p className="eats-admin-pending-panel__title">
              Pending stores ({pendingStores.length}) — select one to continue root admin creation
            </p>
            <div className="eats-admin-pending-grid">
              {pendingStores.map((s) => {
                const isActive = String(s.storeId) === String(activePendingStoreId);
                return (
                  <div
                    key={s.storeId}
                    className={`eats-admin-pending-card${isActive ? " is-active" : ""}`}
                  >
                    <div className="eats-admin-pending-card__name">
                      {s.storeName || `Store ${s.storeId}`}
                    </div>
                    <div className="eats-admin-pending-card__meta">
                      {s.shortCode || `ID: ${s.storeId}`}
                    </div>
                    <div className="eats-admin-pending-card__actions">
                      <button
                        type="button"
                        className={`eats-admin-btn eats-admin-btn--ghost${isActive ? " is-active" : ""}`}
                        onClick={() => selectPendingStore(s.storeId)}
                      >
                        {isActive ? "Selected" : "Use"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="eats-admin-pending-panel__selection">
              Current selection:{" "}
              <strong>
                {activePendingStore
                  ? activePendingStore.storeName || `Store ${activePendingStore.storeId}`
                  : "None"}
              </strong>
            </div>
          </div>
        ) : null}

        <div className="eats-admin-panel">
          {tab === "store" ? (
            <AddStore
              embedded
              createdById={currentUserId}
              onSuccess={handleStoreCreated}
              onClose={() => {}}
            />
          ) : activePendingStore ? (
            <div className="eats-admin-root-admin">
              <h3 className="eats-admin-root-admin__title">
                Create Root Admin for {activePendingStore.storeName || `Store ${activePendingStore.storeId}`}
              </h3>

              <div className="eats-admin-segmented">
                <button
                  type="button"
                  className={`eats-admin-segmented__btn${rootAdminMode === "new" ? " is-active" : ""}`}
                  onClick={clearExistingRootAdminSelection}
                >
                  Create New User
                </button>
                <button
                  type="button"
                  className={`eats-admin-segmented__btn${rootAdminMode === "existing" ? " is-active" : ""}`}
                  onClick={() => setRootAdminMode("existing")}
                >
                  Assign Existing User
                </button>
              </div>

              {rootAdminMode === "existing" ? (
                <RootAdminUserPicker
                  axiosInstance={axiosGroceryInstance}
                  apiPath={GROCERY_API_PATHS.storeRootAdminUsers}
                  selectedUserId={selectedExistingUser?.userId}
                  onSelect={handleSelectExistingRootAdmin}
                  onClear={clearExistingRootAdminSelection}
                />
              ) : null}

              {rootAdminMode === "new" ? (
                <div className="eats-admin-form-grid">
                  <input placeholder="First Name" value={rootAdminForm.firstName} onChange={(e) => setRootAdminForm((p) => ({ ...p, firstName: e.target.value }))} />
                  <input placeholder="Last Name" value={rootAdminForm.lastName} onChange={(e) => setRootAdminForm((p) => ({ ...p, lastName: e.target.value }))} />
                  <input placeholder="Mobile" value={rootAdminForm.mobile} onChange={(e) => setRootAdminForm((p) => ({ ...p, mobile: sanitizeMobileInput(e.target.value) }))} maxLength={10} inputMode="numeric" />
                  <input placeholder="Email" value={rootAdminForm.email} onChange={(e) => setRootAdminForm((p) => ({ ...p, email: e.target.value }))} />
                  <input placeholder="Aadhar Number" value={formatAadharDisplay(rootAdminForm.aadharNo)} onChange={(e) => setRootAdminForm((p) => ({ ...p, aadharNo: sanitizeAadharInput(e.target.value) }))} maxLength={14} inputMode="numeric" />
                  <input type="password" placeholder="Password" value={rootAdminForm.password} onChange={(e) => setRootAdminForm((p) => ({ ...p, password: e.target.value }))} />
                </div>
              ) : selectedExistingUser ? (
                <div className="eats-admin-selected-user">
                  Selected: <strong>{selectedExistingUser.name || `${rootAdminForm.firstName} ${rootAdminForm.lastName}`.trim()}</strong>
                  {" · "}Aadhar: <strong>{formatAadharDisplayOrFallback(selectedExistingUser.aadharNo || rootAdminForm.aadharNo)}</strong>
                  {" · "}Mobile: <strong>{selectedExistingUser.mobile || rootAdminForm.mobile || "—"}</strong>
                </div>
              ) : (
                <div className="eats-admin-hint">
                  Search by Aadhar number above and select a user to assign.
                </div>
              )}

              <div className="eats-admin-actions">
                <button
                  type="button"
                  className="eats-admin-btn eats-admin-btn--primary"
                  onClick={createRootAdminForStore}
                  disabled={creatingRootAdmin || (rootAdminMode === "existing" && !selectedExistingUser?.userId)}
                >
                  {creatingRootAdmin ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="eats-admin-empty-state">Please create/select a store first.</div>
          )}
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminCreateStorePage;
